import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { ConsultThread } from '../../components/medical/ConsultThread';
import { CountryChip, NotesDialog, CloseDialog, PaymentRequestDialog, ScheduleCallDialog, LearnerBriefDialog } from '../../components/medical/ProviderTools';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Inbox, Clock, Star, FileText, MessageSquarePlus, CheckCircle2, Loader2, Trash2, Pencil, RotateCcw, Bell, ClipboardList, StickyNote, CreditCard, Video, User, Salad } from 'lucide-react';
import { LearnerProfileSheet } from '../../components/medical/LearnerProfileSheet';
import { toast } from 'sonner';

const fmtDur = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};
const urgency = (s) => (s == null ? '' : s > 14400 ? 'text-rose-600' : s > 3600 ? 'text-amber-600' : 'text-emerald-600');

const InboxCard = ({ c, active, onClick }) => (
  <button onClick={onClick} data-testid="inbox-card"
    className={`w-full text-left p-3 rounded-xl border transition-colors ${active ? 'border-emerald-400 bg-emerald-50/60' : 'bg-white hover:bg-muted/40'}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-medium truncate">{c.learner_first_name}</span>
        {c.is_returning && <Badge variant="outline" className="text-[10px] gap-0.5 border-amber-300 text-amber-700"><Star className="h-2.5 w-2.5" />{c.prior?.avg_rating_given ?? '–'} · {c.prior?.consult_count}×</Badge>}
      </div>
      {c.unread_count > 0 && <span className="shrink-0 h-5 min-w-5 px-1 rounded-full bg-emerald-600 text-white text-[11px] flex items-center justify-center" data-testid="unread-badge">{c.unread_count}</span>}
    </div>
    <p className="text-xs text-muted-foreground mt-0.5">{c.stage || '—'}{c.week ? ` · wk ${c.week}` : ''}{c.concern ? ` · ${c.concern}` : ''}</p>
    <div className="mt-1"><CountryChip country={c.country} dietPlan={c.diet_plan} compact /></div>
    {/* V73 — the two things the dietician scans this list for */}
    {(c.diet_plan?.entitled || c.diet_form) && (
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {c.diet_plan?.entitled && (
          <Badge variant="outline" className="text-[10px] gap-0.5 border-emerald-300 text-emerald-700"
            data-testid="inbox-diet-plan-label">
            <Salad className="h-2.5 w-2.5" />Diet plan
          </Badge>
        )}
        {c.diet_form?.status && c.diet_form.status !== 'cancelled' && (
          <Badge variant="outline"
            className={`text-[10px] gap-0.5 ${c.diet_form.status === 'submitted'
              ? 'border-sky-300 text-sky-700' : 'border-amber-300 text-amber-700'}`}
            data-testid="inbox-diet-form-label">
            <ClipboardList className="h-2.5 w-2.5" />
            {c.diet_form.status === 'submitted' ? 'Form filled' : 'Form sent'}
          </Badge>
        )}
        {c.diet_form?.flagged && (
          <Badge variant="outline" className="text-[10px] gap-0.5 border-rose-300 text-rose-700"
            data-testid="inbox-diet-form-flag">needs a clinical look</Badge>
        )}
      </div>
    )}
    <p className="text-xs mt-1 line-clamp-2 text-foreground/80">{c.context || c.last_message}</p>
    <div className="flex items-center gap-2 mt-1.5 text-[11px]">
      {c.status === 'waiting' && <span className={`flex items-center gap-0.5 ${urgency(c.waiting_seconds)}`}><Clock className="h-3 w-3" />waiting {fmtDur(c.waiting_seconds)}</span>}
      {c.attached_doc_count > 0 && <span className="flex items-center gap-0.5 text-sky-600"><FileText className="h-3 w-3" />{c.attached_doc_count}</span>}
      {c.episodes_count > 1 && <span className="flex items-center gap-0.5 text-muted-foreground" title="Conversations on this thread"><RotateCcw className="h-3 w-3" />{c.episodes_count}</span>}
      {c.notes_count > 0 && <span className="flex items-center gap-0.5 text-amber-700" title="Internal notes"><StickyNote className="h-3 w-3" />{c.notes_count}</span>}
    </div>
  </button>
);

export default function NutritionistInbox() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('new');
  const [selected, setSelected] = useState(null);
  const [selMeta, setSelMeta] = useState(null);
  const [savedReplies, setSavedReplies] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tool, setTool] = useState(null); // 'brief' | 'notes' | 'payment' | 'call' | 'close'
  const [profileOpen, setProfileOpen] = useState(false);
  const [threadKey, setThreadKey] = useState(0);

  const loadInbox = async () => {
    try { const res = await api.get('/api/medical/inbox'); setData(res.data); } catch (e) { /* */ }
  };
  const loadReplies = async () => {
    try { const res = await api.get('/api/medical/saved-replies'); setSavedReplies(res.data.saved_replies || []); } catch (e) { /* */ }
  };

  useEffect(() => {
    const run = () => api.get('/api/medical/inbox').then(r => setData(r.data)).catch(() => {});
    run();
    api.get('/api/medical/saved-replies').then(r => setSavedReplies(r.data.saved_replies || [])).catch(() => {});
    const t = setInterval(run, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!selected || selMeta?.status !== 'active') return;
    api.get(`/api/medical/consult/${selected}/vault`).then(r => setDocs(r.data.documents || [])).catch(() => {});
  }, [selected, selMeta?.status, threadKey]);

  // keep the selected card's country / diet chips fresh when the inbox re-polls
  useEffect(() => {
    if (!selected || !data) return;
    const all = [...(data.sections?.new || []), ...(data.sections?.active || []), ...(data.sections?.closed || [])];
    const c = all.find(x => x.id === selected);
    if (c) setSelMeta(prev => ({ ...c, status: prev?.status || c.status }));
  }, [data, selected]);

  const openConsult = async (id) => {
    try { await api.post(`/api/medical/consult/${id}/open`); toast.success('Joined the conversation'); loadInbox(); setSelMeta(m => ({ ...m, status: 'active' })); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not open'); }
  };

  const m = data?.metrics || {};
  const sections = data?.sections || { new: [], active: [], closed: [] };
  const list = sections[tab] || [];
  const isOpen = selMeta?.status === 'active';

  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Inbox className="h-6 w-6 text-emerald-600" />Consultations</h1>
          <p className="text-sm text-muted-foreground">The mothers who need you most, first.</p>
        </div>
        <div className="flex items-center gap-4">
          <SavedRepliesManager replies={savedReplies} reload={loadReplies} />
        </div>
      </div>

      {/* metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'Unread', value: m.total_unread ?? 0, icon: Bell, c: 'text-emerald-600' },
          { label: 'Awaiting reply', value: m.awaiting_count ?? 0, icon: Clock, c: 'text-amber-600' },
          { label: 'Avg accept time', value: fmtDur(m.avg_time_to_accept_seconds), icon: CheckCircle2, c: 'text-sky-600' },
          { label: 'Avg reply time', value: fmtDur(m.avg_response_seconds), icon: MessageSquarePlus, c: 'text-violet-600' },
        ].map(k => (
          <div key={k.label} className="rounded-xl border bg-white p-3" data-testid="inbox-metric">
            <k.icon className={`h-4 w-4 ${k.c} mb-1`} />
            <p className="text-lg font-semibold">{k.value}</p>
            <p className="text-[11px] text-muted-foreground">{k.label}</p>
          </div>
        ))}
      </div>

      {/* master-detail */}
      <div className="grid lg:grid-cols-[340px_1fr] gap-4 h-[calc(100vh-300px)] min-h-[420px]">
        {/* list */}
        <div className="flex flex-col rounded-2xl border bg-white overflow-hidden">
          <div className="flex border-b">
            {['new', 'active', 'closed'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`inbox-tab-${t}`}
                className={`flex-1 py-2.5 text-sm font-medium capitalize transition-colors ${tab === t ? 'text-emerald-700 border-b-2 border-emerald-600' : 'text-muted-foreground hover:text-foreground'}`}>
                {t === 'new' ? 'New' : t} {sections[t]?.length ? `(${sections[t].length})` : ''}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {list.length === 0 && <p className="text-center text-sm text-muted-foreground mt-10">Nothing here yet.</p>}
            {list.map(c => <InboxCard key={c.id} c={c} active={selected === c.id} onClick={() => { setSelected(c.id); setSelMeta(c); setDocs([]); }} />)}
          </div>
        </div>

        {/* detail */}
        <div className="rounded-2xl border bg-white overflow-hidden flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="border-b p-3 flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <button
                    onClick={() => setProfileOpen(true)}
                    className="font-medium truncate hover:underline decoration-dotted underline-offset-4 text-left"
                    title="See her profile"
                    data-testid="learner-name-btn"
                  >
                    {selMeta?.learner_first_name} <span className="text-xs text-muted-foreground">· {selMeta?.stage}{selMeta?.week ? ` wk ${selMeta.week}` : ''}</span>
                  </button>
                  <div className="mt-0.5"><CountryChip country={selMeta?.country} dietPlan={selMeta?.diet_plan} /></div>
                  {selMeta?.context && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{selMeta.context}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end" data-testid="consult-toolbar">
                  {docs.length > 0 && <Badge variant="outline" className="gap-1 text-sky-700"><FileText className="h-3 w-3" />{docs.length} doc{docs.length > 1 ? 's' : ''}</Badge>}
                  {selMeta?.status === 'waiting' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openConsult(selected)} data-testid="open-consult-btn">Open & reply</Button>}
                  {isOpen && <Button size="sm" variant="outline" onClick={() => setProfileOpen(true)} title="Her profile" data-testid="learner-profile-btn"><User className="h-4 w-4 mr-1" />Profile</Button>}
                  {isOpen && <Button size="sm" variant="outline" onClick={() => setTool('brief')} title="Medical brief" data-testid="learner-brief-btn"><ClipboardList className="h-4 w-4 mr-1" />Brief</Button>}
                  <Button size="sm" variant="outline" onClick={() => setTool('notes')} title="Internal notes" data-testid="provider-notes-btn"><StickyNote className="h-4 w-4 mr-1" />Notes</Button>
                  {isOpen && <Button size="sm" variant="outline" onClick={() => setTool('payment')} title="Request a payment" data-testid="payment-request-btn"><CreditCard className="h-4 w-4 mr-1" />Payment</Button>}
                  {isOpen && <Button size="sm" variant="outline" onClick={() => setTool('call')} title="Schedule a video consultation" data-testid="schedule-call-btn"><Video className="h-4 w-4 mr-1" />Video call</Button>}
                  {isOpen && <Button size="sm" variant="outline" onClick={() => setTool('close')} data-testid="close-consult-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Close</Button>}
                </div>
              </div>
              {docs.length > 0 && (
                <div className="px-3 py-1.5 border-b bg-sky-50/50 flex gap-2 overflow-x-auto" data-testid="attached-docs">
                  {docs.map(d => <span key={d.id} className="shrink-0 text-[11px] flex items-center gap-1 text-sky-800"><FileText className="h-3 w-3" />{d.file_name}</span>)}
                </div>
              )}
              <div className="flex-1 min-h-0">
                <ConsultThread key={`${selected}-${threadKey}`} consultId={selected} composer={selMeta?.status !== 'waiting'} savedReplies={savedReplies}
                  onMeta={(meta) => setSelMeta(prev => ({ ...prev, status: meta.status }))} />
              </div>
              <LearnerBriefDialog consultId={selected} open={tool === 'brief'} onOpenChange={(v) => !v && setTool(null)} />
              <LearnerProfileSheet
                key={selected}
                consultId={selected}
                open={profileOpen}
                onOpenChange={setProfileOpen}
              />
              <NotesDialog consultId={selected} open={tool === 'notes'} onOpenChange={(v) => !v && setTool(null)} canAdd={isOpen} />
              <PaymentRequestDialog consultId={selected} open={tool === 'payment'} onOpenChange={(v) => !v && setTool(null)}
                suggestedCurrency={selMeta?.country?.suggested_currency} onCreated={() => setThreadKey(k => k + 1)} />
              <ScheduleCallDialog consultId={selected} open={tool === 'call'} onOpenChange={(v) => !v && setTool(null)} onScheduled={() => setThreadKey(k => k + 1)} />
              <CloseDialog consultId={selected} open={tool === 'close'} onOpenChange={(v) => !v && setTool(null)}
                onClosed={() => { loadInbox(); setSelMeta(m => ({ ...m, status: 'completed' })); setTab('closed'); }} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SavedRepliesManager({ replies, reload }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setTitle(''); setBody(''); setEditing(null); };
  const save = async () => {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      if (editing) await api.put(`/api/medical/saved-replies/${editing}`, { title, body });
      else await api.post('/api/medical/saved-replies', { title, body });
      toast.success('Saved'); reset(); reload();
    } catch { toast.error('Could not save'); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/api/medical/saved-replies/${id}`); reload(); } catch { /* */ } };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="saved-replies-btn"><MessageSquarePlus className="h-4 w-4 mr-1" />Saved replies</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Saved replies</DialogTitle><DialogDescription>Quick canned answers. Selecting one fills your message box so you can edit before sending.</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-52 overflow-y-auto">
          {replies.length === 0 && <p className="text-sm text-muted-foreground">No saved replies yet. Create one below to reply faster.</p>}
          {replies.map(r => (
            <div key={r.id} className="flex items-start gap-2 p-2 rounded-lg border">
              <div className="flex-1 min-w-0"><p className="text-sm font-medium">{r.title}</p><p className="text-xs text-muted-foreground line-clamp-2">{r.body}</p></div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(r.id); setTitle(r.title); setBody(r.body); }}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => del(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
        <div className="space-y-2 border-t pt-3">
          <Input placeholder="Title (e.g. Hydration tip)" value={title} onChange={e => setTitle(e.target.value)} data-testid="saved-reply-title" />
          <Textarea placeholder="Reply text — you can edit it before sending." value={body} onChange={e => setBody(e.target.value)} rows={3} data-testid="saved-reply-body" />
        </div>
        <DialogFooter>
          {editing && <Button variant="ghost" onClick={reset}>Cancel edit</Button>}
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-reply-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Update' : 'Add reply')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
